import { apiClient } from './client';
import { calculateMD5, generateMd5FromUrl } from '../../lib/assetTypeDetection';

// Types for assets API
export interface AssetDto {
  id?: number;
  userId?: number;
  uuid: string;
  type: 'file' | 'url';
  md5?: string;
  meta: {
    filename: string;
    description?: string;
    contentType: string;
    size: number;
    tags?: string[];
    assetType?: string;
    url?: string;
    [key: string]: any;
  };
  state?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  previewUrl?: string; // To be populated by a separate call
}

export interface AssetUploadResponse {
  asset: AssetDto;
  uploadUrl?: string;
}

export interface AssetUpdateRequest {
  meta?: {
    [key: string]: any;
  };
}

/**
 * Assets API client
 */
export const assetsApi = {
  /**
   * Check for duplicates and get upload URL for a file
   * @param file The file to upload
   * @returns The upload request result including duplicate check and pre-signed URL
   */
  requestUpload: async (file: File): Promise<{ 
    md5: string; 
    uuid: string;
    filename: string;
    size: number;
    contentType: string;
    isDuplicate: boolean; 
    r2Key?: string;
    uploadUrl?: string;
    duplicateInfo?: {
      filename: string;
      uploadDate: string;
      uuid: string;
    } 
  }> => {
    // Calculate MD5 hash client-side
    const md5 = await calculateMD5(file);
    
    const { data } = await apiClient.post<{
      md5: string;
      uuid: string;
      filename: string;
      size: number;
      contentType: string;
      isDuplicate: boolean;
      r2Key?: string;
      uploadUrl?: string;
      duplicateInfo?: {
        filename: string;
        uploadDate: string;
        uuid: string;
      }
    }>('/api/assets/upload-request', {
      filename: file.name,
      contentType: file.type,
      size: file.size,
      md5: md5
    });
    
    return data;
  },

  /**
   * Check for duplicates for a URL asset
   * @param url The URL to check
   * @returns The duplicate check result
   */
  checkUrlDuplicate: async (url: string): Promise<{ 
    md5: string; 
    uuid: string;
    filename: string;
    size: number;
    contentType: string;
    url: string;
    isDuplicate: boolean; 
    duplicateInfo?: {
      filename: string;
      uploadDate: string;
      uuid: string;
    } 
  }> => {
    // Calculate MD5 hash of the URL
    const md5 = generateMd5FromUrl(url);
    
    // For URLs, we don't need pre-signed URLs, just duplicate checking
    return {
      md5,
      uuid: `ast_${crypto.randomUUID()}`,
      filename: url.split('/').pop() || 'url-asset',
      size: 0,
      contentType: 'text/url',
      url,
      isDuplicate: false, // For now, assume URLs are not duplicates
    };
  },

  /**
   * Upload file directly to R2 using pre-signed URL
   * @param file The file to upload
   * @param uploadUrl The pre-signed URL from requestUpload
   * @returns Promise that resolves when upload is complete
   */
  uploadToR2: async (file: File, uploadUrl: string): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  },

  /**
   * Get all assets for the current user
   */
  getAll: async (): Promise<AssetDto[]> => {
    const { data } = await apiClient.get<AssetDto[]>('/api/assets');
    return data || [];
  },

  /**
   * Get a specific asset by UUID
   */
  getAsset: async (assetId: string): Promise<AssetDto> => {
    const { data } = await apiClient.get<AssetDto>(`/api/assets/${encodeURIComponent(assetId)}`);
    return data;
  },

  /**
   * Complete the asset upload process after verification
   * @param uuid The UUID from the verification step
   * @param md5 The MD5 hash from the verification step
   * @param metadata Metadata for the asset
   * @param forceDuplicate Optional flag to force upload even if duplicate exists
   * @param assetType The type of asset ('file' or 'url')
   */
  createAsset: async (
    uuid: string,
    md5: string,
    r2Key: string,
    metadata: Record<string, any>,
    assetType: 'file' | 'url' = 'file'
  ): Promise<AssetDto> => {
    // Add force duplicate flag if true
    try {
      // Create FormData with MD5, metadata, and asset type
      const formData = new FormData();
      formData.append('md5', md5);
      formData.append('meta', JSON.stringify(metadata));
      formData.append('type', assetType);
      formData.append('uuid', uuid);
      formData.append('r2Key', r2Key);
      
      const response = await apiClient.post<AssetDto>(
        `/api/assets`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      // If we get a 409 Conflict, it means a duplicate was found but forceDuplicate was false
      if (error.response && error.response.status === 409 && error.response.data.duplicateAsset) {
        throw new Error('Duplicate asset found. Set forceDuplicate to true to upload anyway.');
      }
      throw error;
    }
  },

  /**
   * Update an existing asset's metadata
   * @param assetId The UUID of the asset to update
   * @param meta The metadata updates to apply
   */
  updateAsset: async (
    assetId: string, 
    meta: Record<string, any>
  ): Promise<{ success: boolean }> => {
    const { data } = await apiClient.put<{ success: boolean }>(
      `/api/assets/${encodeURIComponent(assetId)}`,
      { meta }
    );
    
    return data;
  },

  /**
   * Update an existing asset's content (file replacement)
   * @param assetId The UUID of the asset to update
   * @param file The new file to upload
   */
  updateAssetContent: async (
    assetId: string, 
    file: File
  ): Promise<AssetDto> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await apiClient.patch<AssetDto>(
      `/api/assets/${encodeURIComponent(assetId)}/content`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return data;
  },

  /**
   * Get the content of an asset
   * @param assetId The UUID of the asset
   * @returns A URL to the asset content
   */
  getAssetContentUrl: (assetId: string): string => {
    return `${apiClient.defaults.baseURL}/api/assets/${encodeURIComponent(assetId)}/content`;
  },

  /**
   * Download asset content
   * @param assetId The UUID of the asset
   * @returns The asset content as a Blob
   */
  downloadAssetContent: async (assetId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      `/api/assets/${encodeURIComponent(assetId)}/content`,
      { responseType: 'blob' }
    );
    return data;
  },

  /**
   * Delete an asset
   * @param assetId The UUID of the asset to delete
   */
  deleteAsset: async (assetId: string): Promise<void> => {
    await apiClient.delete(`/api/assets/${encodeURIComponent(assetId)}`);
  },

  /**
   * Get all tags for an asset
   * @param assetId The UUID of the asset
   */
  getAssetTags: async (assetId: string): Promise<string[]> => {
    const { data } = await apiClient.get<string[]>(`/api/assets/${encodeURIComponent(assetId)}/tags`);
    return data || [];
  },

  /**
   * Add a tag to an asset
   * @param assetId The UUID of the asset
   * @param tag The tag to add
   */
  addTagToAsset: async (assetId: string, tags: string[]): Promise<void> => {
    await apiClient.post(`/api/assets/${encodeURIComponent(assetId)}/tags`, { tags });
  },

  /**
   * Remove a tag from an asset
   * @param assetId The UUID of the asset
   * @param tag The tag to remove
   */
  removeTagFromAsset: async (assetId: string, tag: string): Promise<void> => {
    await apiClient.post(`/api/assets/${encodeURIComponent(assetId)}/tags/delete`, { tag });
  },

  /**
   * Get all unique tags, filtered by type
   * @param type The type of tags to fetch (e.g., 'documents', 'assets')
   */
  getAllTags: async (type: 'documents' | 'assets' = 'documents'): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/api/tags?type=${type}`);
    // Ensure we always return an array, even if the API response is malformed.
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Get a temporary, signed URL for an asset preview
   * @param assetId The UUID of the asset
   */
  getAssetPreviewUrl: async (assetId: string): Promise<string> => {
    const response = await apiClient.get(`/api/assets/${assetId}/preview-url`);
    return response.data.previewUrl;
  },
};
